import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment, { Moment } from "moment";
import DatePicker from "react-date-picker";
import { Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PickerMode, SocialProviderId } from "../../../cores/socialPost/config";
import { SOCIAL_ACTION_TYPES } from "../../../cores/socialPost/consts/socialActionType.constant";
import { SOCIAL_POST_TYPES } from "../../../cores/socialPost/consts/socialPostType.constant";
import { PostType } from "../../../cores/socialPost/enums/postType";
import { useGlobalCore } from "../../../cores/globals";
import { useSocialPostCore } from "../../../cores/socialPost";
import { useTemplateCore } from "../../../cores/templates";
import { useMeCore } from "../../../cores/me";
import {
  ADD_MENTION_MODAL,
  ADD_SOCIAL_IMAGE_MODAL,
  ADD_SOCIAL_VIDEO_MODAL,
  EMOJI_FORM,
  HASHTAGS_SOCIAL_FORM,
  OPENAI_SOCIAL_FORM,
  TEMPLATES_SOCIAL_FORM,
} from "../../../constants/modal.constant";
import { SocialType } from "../../../cores/socialPost/interfaces/postError";
import { ModernSocialPostSelector } from "../socialPost/selector/ModernSocialPostSelector";
import { ModernTextEditor } from "../socialPost/creator/ModernTextEditor";
import { ModernPostButtons } from "../socialPost/creator/ModernPostButtons";
import { UrlShortenerOptions } from "../socialPost/creator/UrlShortenerOptions";
import { SocialPostPreview } from "../socialPost/preview/SocialPostPreview";
import { EmojiForm } from "../EmojiForm";
import { TiktokDirectPostPanel } from "../social/Tiktok/TiktokDirectPostPanel";
import { YoutubeDirectPostPanel } from "../social/Youtube/YoutubeDirectPostPanel";
import "../socialPost/creator/ModernPostContainer.scss";
import { scheduleDateStringForApiFromDateAndHour } from "../../../cores/socialPost/helpers/scheduleDateForApi";
import { UpdateLoadingCard } from "../UpdateLoadingCard";
import { INSTAGRAM_POST_LOADING } from "../../../constants/loading.constant";
import { toast } from "react-toastify";

/**
 * Momento por defecto al programar para el día de hoy: +1 h respecto a ahora.
 * Si eso cruza medianoche, se usa 23:59 del mismo día; si no aplica, +1 minuto.
 */
function defaultMomentForTodayPickedDate(pickedDate: Date): Moment {
  const pickedStart = moment(pickedDate).startOf("day");
  const oneHourLater = moment().add(1, "hour").second(0).millisecond(0);
  if (oneHourLater.isSame(pickedStart, "day")) {
    return oneHourLater;
  }
  const endOfDay = pickedStart
    .clone()
    .hour(23)
    .minute(59)
    .second(0)
    .millisecond(0);
  if (endOfDay.isAfter(moment())) {
    return endOfDay;
  }
  return moment().add(1, "minute");
}

/** Garantiza fecha/hora futura (mínimo ahora + 1 h cuando el valor cae en el pasado). */
function ensureFutureScheduleMoment(candidate: Moment): Moment {
  const now = moment();
  if (candidate.isAfter(now)) {
    return candidate.clone().second(0).millisecond(0);
  }
  return now.clone().add(1, "hour").second(0).millisecond(0);
}

interface Props {
  selectedDate?: Moment;
  onChangeSelectedDate?: (value: Moment) => void;
  onScheduledSuccess?: () => void;
  onSavedSuccess?: () => void;
  onDeletedSuccess?: () => void;
  /** Recarga posts del calendario (mismo rango y filtros que la barra). */
  onPostsRefresh?: () => void;
}

export const SocialCalendarPostComposer = ({
  selectedDate,
  onChangeSelectedDate,
  onScheduledSuccess,
  onSavedSuccess,
  onDeletedSuccess,
  onPostsRefresh,
}: Props) => {
  const { company } = useMeCore();
  const { turnOnModal, isLoading } = useGlobalCore();
  const {
    getHashtags,
    addEmoji,
    updateMentions,
    inputCursorIndex,
    text,
    selectedPost,
    selectPost,
    setDuplicateScheduleSourcePostId,
    duplicateScheduleSourcePostId,
    action,
    postType,
    video,
    socialProviders,
    resetComposerDraft,
  } = useSocialPostCore();
  /** Evita que selectPost (p. ej. al cambiar fecha) pise el texto del textarea con post.post.text viejo. */
  const draftTextRef = useRef(text);
  draftTextRef.current = text;
  const { getSocialTemplates } = useTemplateCore();
  const [picker, setPicker] = useState<PickerMode>(PickerMode.Off);
  const [showPreview, setShowPreview] = useState(true);
  const [isDuplicatingAnim, setIsDuplicatingAnim] = useState(false);
  const [dateValue, setDateValue] = useState<Date>(
    selectedDate ? selectedDate.toDate() : new Date()
  );
  const [hourValue, setHourValue] = useState<string>(
    selectedDate ? selectedDate.format("HH:mm") : moment().format("HH:mm")
  );

  const selectedMoment = useMemo(() => {
    const [h, m] = hourValue.split(":").map((x) => Number(x));
    return moment(dateValue)
      .hour(Number.isFinite(h) ? h : 0)
      .minute(Number.isFinite(m) ? m : 0)
      .second(0)
      .millisecond(0);
  }, [dateValue, hourValue]);

  /** Idéntico a PostForm (modal del planificador): el backend procesa la cola con el mismo formato. */
  const scheduleAtForApi = useMemo(
    () =>
      scheduleDateStringForApiFromDateAndHour(dateValue, hourValue),
    [dateValue, hourValue]
  );

  /** Para validar “Programar” sin depender de parsear el .toString() del calendario. */
  const scheduleAtTimestamp = useMemo(
    () => (selectedMoment.isValid() ? selectedMoment.valueOf() : undefined),
    [selectedMoment]
  );

  const baselineMomentRef = useRef<Moment>(selectedMoment.clone());
  const [baselineVersion, setBaselineVersion] = useState(0);

  const hasUserChangedDateTime = useMemo(() => {
    return !selectedMoment.isSame(baselineMomentRef.current, "minute");
  }, [selectedMoment, baselineVersion]);

  const syncSelectedDate = useCallback(
    (nextMoment: Moment) => {
      onChangeSelectedDate?.(nextMoment);
      if (selectedPost) {
        selectPost({
          ...selectedPost,
          post: {
            ...selectedPost.post,
            text: draftTextRef.current,
            scheduleDate: scheduleDateStringForApiFromDateAndHour(
              nextMoment.toDate(),
              nextMoment.format("HH:mm")
            ),
          },
        });
      }
    },
    [onChangeSelectedDate, selectedPost, selectPost]
  );

  const applyScheduleMoment = useCallback(
    (nextMoment: Moment, options?: { asBaseline?: boolean; warnIfAdjusted?: boolean }) => {
      const safe = ensureFutureScheduleMoment(nextMoment);
      if (
        options?.warnIfAdjusted &&
        !safe.isSame(nextMoment, "minute")
      ) {
        toast.info("La hora debe ser futura. Se ajustó a +1 hora desde ahora.");
      }
      setDateValue(safe.toDate());
      setHourValue(safe.format("HH:mm"));
      if (options?.asBaseline) {
        baselineMomentRef.current = safe.clone();
        setBaselineVersion((v) => v + 1);
      }
      syncSelectedDate(safe);
      return safe;
    },
    [syncSelectedDate]
  );

  useEffect(() => {
    if (!selectedDate) return;

    let next = selectedDate.clone().second(0).millisecond(0);
    let asBaseline = false;

    if (!selectedPost) {
      const looksLikeCalendarMidnight =
        next.hour() === 0 && next.minute() === 0;
      if (next.isSame(moment(), "day") && looksLikeCalendarMidnight) {
        next = defaultMomentForTodayPickedDate(next.toDate());
        asBaseline = true;
      } else if (!next.isAfter(moment())) {
        next = ensureFutureScheduleMoment(next);
        asBaseline = true;
      } else {
        asBaseline = true;
      }
    }

    setDateValue((prev) =>
      moment(prev).isSame(next, "minute") ? prev : next.toDate()
    );
    setHourValue((prev) => {
      const formatted = next.format("HH:mm");
      return prev === formatted ? prev : formatted;
    });

    if (asBaseline) {
      baselineMomentRef.current = next.clone();
      setBaselineVersion((v) => v + 1);
    }

    if (!selectedDate.isSame(next, "minute")) {
      onChangeSelectedDate?.(next);
    }
    // Solo reaccionar a cambios de fecha externa / modo edición
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedPost]);

  const handleDateChange = useCallback(
    (v: Date | Date[] | null) => {
      if (!v || Array.isArray(v)) return;
      const switchingToToday =
        moment(v).isSame(moment(), "day") &&
        !moment(dateValue).isSame(moment(), "day");

      let nextMoment: Moment;
      if (switchingToToday) {
        nextMoment = defaultMomentForTodayPickedDate(v);
      } else {
        const [h, m] = hourValue.split(":").map((x) => Number(x));
        nextMoment = moment(v)
          .hour(Number.isFinite(h) ? h : 0)
          .minute(Number.isFinite(m) ? m : 0)
          .second(0)
          .millisecond(0);
      }
      applyScheduleMoment(nextMoment, { warnIfAdjusted: true });
    },
    [dateValue, hourValue, applyScheduleMoment]
  );

  const handleTimeChange = useCallback(
    (value: string) => {
      const [h, m] = value.split(":").map((x) => Number(x));
      const nextMoment = moment(dateValue)
        .hour(Number.isFinite(h) ? h : 0)
        .minute(Number.isFinite(m) ? m : 0)
        .second(0)
        .millisecond(0);
      applyScheduleMoment(nextMoment, { warnIfAdjusted: true });
    },
    [dateValue, applyScheduleMoment]
  );

  const openMentionsModal = useCallback(() => {
    setPicker(PickerMode.Mention);
    turnOnModal(ADD_MENTION_MODAL, true);
  }, [turnOnModal]);

  const openEmojiModal = useCallback(() => {
    setPicker(PickerMode.Emoji);
    turnOnModal(
      EMOJI_FORM,
      <EmojiForm
        onAdding={(emoji: any) => {
          const unifieds = emoji.unified.split("-");
          const emojiCode = unifieds
            .map((unified: any) => String.fromCodePoint(Number(`0x${unified}`)))
            .join("");
          addEmoji(emojiCode);
          updateMentions(emojiCode.length, inputCursorIndex as number);
        }}
      />
    );
  }, [turnOnModal, addEmoji, updateMentions, inputCursorIndex]);

  const openImagePickerModal = useCallback(() => {
    setPicker(PickerMode.Image);
    turnOnModal(ADD_SOCIAL_IMAGE_MODAL, true);
  }, [turnOnModal]);

  const openVideoPickerModal = useCallback(() => {
    setPicker(PickerMode.Video);
    turnOnModal(ADD_SOCIAL_VIDEO_MODAL, true);
  }, [turnOnModal]);

  const openHashtagModal = useCallback(() => {
    setPicker(PickerMode.HashTag);
    getHashtags(company?.id!);
    turnOnModal(HASHTAGS_SOCIAL_FORM, true);
  }, [turnOnModal, getHashtags, company]);

  const openTemplateModal = useCallback(() => {
    setPicker(PickerMode.Off);
    getSocialTemplates(company?.id!);
    turnOnModal(TEMPLATES_SOCIAL_FORM, true);
  }, [turnOnModal, getSocialTemplates, company]);

  const openOpenAiModal = useCallback(() => {
    turnOnModal(OPENAI_SOCIAL_FORM, true);
  }, [turnOnModal]);

  /** Duplicar: mismo contenido, se pueden elegir más redes, hora = ahora + 1 h. */
  const beginDuplicateSchedule = useCallback(() => {
    if (!selectedPost?.post?.id || isDuplicatingAnim) return;
    setIsDuplicatingAnim(true);
    window.setTimeout(() => {
      setDuplicateScheduleSourcePostId(selectedPost.post.id);
      applyScheduleMoment(moment().add(1, "hour"), { asBaseline: true });
      window.setTimeout(() => setIsDuplicatingAnim(false), 120);
    }, 720);
  }, [
    selectedPost?.post?.id,
    isDuplicatingAnim,
    setDuplicateScheduleSourcePostId,
    applyScheduleMoment,
  ]);

  const { hourPart, minutePart } = useMemo(() => {
    const [h, m] = hourValue.split(":").map((x) => Number(x));
    return {
      hourPart: Number.isFinite(h) ? Math.min(23, Math.max(0, h)) : 0,
      minutePart: Number.isFinite(m) ? Math.min(59, Math.max(0, m)) : 0,
    };
  }, [hourValue]);

  const hourOptions = useMemo(
    () => Array.from({ length: 24 }, (_, i) => i),
    []
  );
  const minuteOptions = useMemo(
    () => Array.from({ length: 60 }, (_, i) => i),
    []
  );

  const instagramReelScheduleProgramming = useMemo(() => {
    if (action !== SOCIAL_ACTION_TYPES.SCHEDULE) return false;
    if (!isLoading(INSTAGRAM_POST_LOADING)) return false;
    const hasInstagram = socialProviders.some(
      (sp: any) =>
        sp?.id === SocialProviderId.Instagram ||
        sp?.name === SocialProviderId.Instagram
    );
    if (!hasInstagram) return false;
    const isReel =
      postType === PostType.REEL ||
      selectedPost?.post?.type === SOCIAL_POST_TYPES.VIDEO;
    if (!isReel) return false;
    return Boolean(
      video ||
        (selectedPost?.post?.type === SOCIAL_POST_TYPES.VIDEO &&
          (selectedPost?.media?.length ?? 0) > 0)
    );
  }, [
    action,
    isLoading,
    socialProviders,
    postType,
    selectedPost?.post?.type,
    selectedPost?.media?.length,
    video,
  ]);

  return (
    <div className="modern-multiposter px-3 pb-3">
      <div className="d-flex justify-content-start align-items-center mb-2 px-2">
        <div className="d-flex align-items-center">
          <Button
            type="button"
            className={`social-calendar-composer__preview-toggle ${
              showPreview ? "is-active" : ""
            }`}
            onClick={() => setShowPreview((v) => !v)}
          >
            <FontAwesomeIcon icon={["far", "eye"]} className="mr-2" />
            Vista previa
          </Button>
        </div>
      </div>
      <div
        className={`social-calendar-composer__layout ${
          showPreview ? "social-calendar-composer__layout--with-preview" : ""
        } ${isDuplicatingAnim ? "social-calendar-composer__layout--duplicating" : ""}`}
      >
        <div
          className={`social-calendar-composer__editor ${
            isDuplicatingAnim ? "is-duplicating" : ""
          }`}
        >
          {isDuplicatingAnim && (
            <>
              <div
                className="social-calendar-composer__duplicate-ghost social-calendar-composer__duplicate-ghost--a"
                aria-hidden
              />
              <div
                className="social-calendar-composer__duplicate-ghost social-calendar-composer__duplicate-ghost--b"
                aria-hidden
              >
                <span>Duplicando publicación</span>
              </div>
            </>
          )}
          <div className="modern-post-container">
            <div className="social-selector-section">
              <ModernSocialPostSelector
                socialType={SocialType.Post}
                hideTitle
                lockProviders={Boolean(
                  selectedPost && !duplicateScheduleSourcePostId
                )}
              />
            </div>

            {company?.id != null && (
              <TiktokDirectPostPanel companyId={String(company.id)} />
            )}
            {company?.id != null && (
              <YoutubeDirectPostPanel companyId={String(company.id)} />
            )}

            <ModernTextEditor
              activePicker={picker}
              onClickEmoji={openEmojiModal}
              onClickHashtag={openHashtagModal}
              onClickMention={openMentionsModal}
              onClickImages={openImagePickerModal}
              onClickVideo={openVideoPickerModal}
              onClickTemplate={openTemplateModal}
              onClickOpenAi={openOpenAiModal}
              socialType={SocialType.Post}
            />
            <UrlShortenerOptions />
            {instagramReelScheduleProgramming ? (
              <UpdateLoadingCard
                loadingId={INSTAGRAM_POST_LOADING}
                text="Programando"
                className="mb-0 px-3"
              />
            ) : null}
            <div
              className={`social-calendar-composer__publish-datetime px-3 py-2 ${
                duplicateScheduleSourcePostId
                  ? "social-calendar-composer__publish-datetime--highlight"
                  : ""
              }`}
            >
              {duplicateScheduleSourcePostId && (
                <p className="small text-muted mb-2">
                  {selectedPost?.post?.postAt
                    ? "Republicando publicación: elige redes, día y hora futura, y pulsa Programar o Publicar ahora."
                    : "Duplicando publicación: puedes elegir otras redes sincronizadas o conectar más. Elige el día y la hora (futura) y pulsa Programar, o Publicar ahora."}
                </p>
              )}
              <span className="social-calendar-composer__publish-label mr-2">
                Día y hora de publicación:
              </span>
              <div className="social-calendar-composer__datetime">
                <DatePicker
                  value={dateValue}
                  onChange={handleDateChange}
                  locale="es-ES"
                  calendarType="ISO 8601"
                  /* No usar `new Date()`: incluye la hora y deshabilita “hoy” en el calendario. */
                  minDate={moment().startOf("day").toDate()}
                  clearIcon={null}
                  format="dd/MM/y"
                  calendarIcon={null}
                  calendarClassName="social-calendar-composer__calendar"
                  className="social-calendar-composer__date-input"
                />
                <div
                  className="social-calendar-composer__time-group"
                  role="group"
                  aria-label="Hora de publicación"
                >
                  <select
                    className="social-calendar-composer__time-select"
                    value={hourPart}
                    onChange={(e) => {
                      const h = Number(e.target.value);
                      handleTimeChange(
                        `${String(h).padStart(2, "0")}:${String(minutePart).padStart(2, "0")}`
                      );
                    }}
                  >
                    {hourOptions.map((h) => (
                      <option key={h} value={h}>
                        {String(h).padStart(2, "0")}
                      </option>
                    ))}
                  </select>
                  <span className="social-calendar-composer__time-sep" aria-hidden>
                    :
                  </span>
                  <select
                    className="social-calendar-composer__time-select social-calendar-composer__time-select--min"
                    value={minutePart}
                    onChange={(e) => {
                      const m = Number(e.target.value);
                      handleTimeChange(
                        `${String(hourPart).padStart(2, "0")}:${String(m).padStart(2, "0")}`
                      );
                    }}
                  >
                    {minuteOptions.map((m) => (
                      <option key={m} value={m}>
                        {String(m).padStart(2, "0")}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <ModernPostButtons
              repost={false}
              from="calendar"
              onReset={() => {
                setPicker(PickerMode.Off);
                /* reset() borraría socialPosts en Redux y vacía el calendario hasta el siguiente fetch */
                resetComposerDraft();
              }}
              scheduleAt={scheduleAtForApi}
              scheduleAtTimestamp={scheduleAtTimestamp}
              scheduleDirty={hasUserChangedDateTime}
              onScheduledSuccess={onScheduledSuccess}
              onSavedSuccess={onSavedSuccess}
              onDeletedSuccess={onDeletedSuccess}
              onPostsRefresh={onPostsRefresh}
              onBeginDuplicateSchedule={beginDuplicateSchedule}
            />
          </div>
        </div>

        {showPreview && (
          <div className="social-calendar-composer__preview">
            <div className="social-calendar-composer__preview-card">
              <SocialPostPreview compact />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

